import { createSlice, PayloadAction, createAsyncThunk } from '@reduxjs/toolkit';
import {
  fetchPrimeArtifacts,
  fetchPrimeArtifactsHierarchy,
  fetchPrimeArtifactStates,
  fetchPrimeArtifactTransitions,
  fetchPrimeProcesses,
  createPrimeModel,
  updatePrimeModel,
  deletePrimeModel,
  fetchArtifactTypes,
  createPrimeArtifactTransition,
  updatePrimeArtifactTransition,
  deletePrimeArtifactTransition,
  createPrimeProcess,
  updatePrimeProcess,
  deletePrimeProcess,
  updatePrimeArtifact,
} from '../actions/primeArtifactActions';
import {
  PrimeArtifactState,
  PrimeArtifactStateTransition,
  PrimeProcess,
  PrimeModelCreationData,
} from '../../types/primeModel';
import { PrimeArtifact } from '../../types/primeArtifact';
import { PrimeArtifactService } from '../../services/primeArtifactService';

interface PrimeArtifactSliceState {
  primeArtifacts: PrimeArtifact[];
  flatArtifacts: PrimeArtifact[];
  primeArtifactStates: PrimeArtifactState[];
  primeArtifactTransitions: PrimeArtifactStateTransition[];
  primeProcesses: PrimeProcess[];
  artifactTypes: { id: string; name: string }[];
  loading: boolean;
  error: string | null;
  hierarchyLoading: boolean;
  hierarchyError: string | null;
}

const initialState: PrimeArtifactSliceState = {
  primeArtifacts: [],
  flatArtifacts: [],
  primeArtifactStates: [],
  primeArtifactTransitions: [],
  primeProcesses: [],
  artifactTypes: [],
  loading: false,
  error: null,
  hierarchyLoading: false,
  hierarchyError: null,
};

// Fetch flat list of artifacts
export const fetchFlatArtifacts = createAsyncThunk(
  'primeArtifacts/fetchFlatArtifacts',
  async () => {
    const response = await PrimeArtifactService.getAll();
    return response;
  }
);

const primeArtifactSlice = createSlice({
  name: 'primeArtifacts',
  initialState,
  reducers: {
    clearPrimeArtifactState: (state) => {
      state.primeArtifacts = [];
      state.primeArtifactStates = [];
      state.primeArtifactTransitions = [];
      state.primeProcesses = [];
      state.error = null;
      state.hierarchyError = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Prime Artifacts Hierarchy
      .addCase(fetchPrimeArtifactsHierarchy.pending, (state) => {
        state.hierarchyLoading = true;
        state.hierarchyError = null;
      })
      .addCase(
        fetchPrimeArtifactsHierarchy.fulfilled,
        (state, action: PayloadAction<PrimeArtifact[]>) => {
          state.hierarchyLoading = false;
          state.primeArtifacts = action.payload;
        }
      )
      .addCase(fetchPrimeArtifactsHierarchy.rejected, (state, action) => {
        state.hierarchyLoading = false;
        state.hierarchyError = action.error.message || 'Failed to fetch prime artifacts hierarchy';
      })

      // Fetch Flat Artifacts
      .addCase(fetchFlatArtifacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFlatArtifacts.fulfilled, (state, action: PayloadAction<PrimeArtifact[]>) => {
        state.loading = false;
        state.flatArtifacts = action.payload;
      })
      .addCase(fetchFlatArtifacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch flat artifacts';
      })

      // Fetch Prime Artifacts
      .addCase(fetchPrimeArtifacts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrimeArtifacts.fulfilled, (state, action: PayloadAction<PrimeArtifact[]>) => {
        state.loading = false;
        state.primeArtifacts = action.payload;
      })
      .addCase(fetchPrimeArtifacts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch prime artifacts';
      })

      // Fetch Prime Artifact States
      .addCase(fetchPrimeArtifactStates.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchPrimeArtifactStates.fulfilled,
        (state, action: PayloadAction<PrimeArtifactState[]>) => {
          console.log('[primeArtifactSlice] Storing states in Redux:', action.payload);
          state.loading = false;
          state.primeArtifactStates = action.payload;
        }
      )
      .addCase(fetchPrimeArtifactStates.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch prime artifact states';
      })

      // Fetch Prime Artifact Transitions
      .addCase(fetchPrimeArtifactTransitions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        fetchPrimeArtifactTransitions.fulfilled,
        (state, action: PayloadAction<PrimeArtifactStateTransition[]>) => {
          state.loading = false;
          state.primeArtifactTransitions = action.payload;
        }
      )
      .addCase(fetchPrimeArtifactTransitions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch prime artifact transitions';
      })

      // Fetch Prime Processes
      .addCase(fetchPrimeProcesses.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPrimeProcesses.fulfilled, (state, action: PayloadAction<PrimeProcess[]>) => {
        state.loading = false;
        state.primeProcesses = action.payload;
      })
      .addCase(fetchPrimeProcesses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch prime processes';
      })

      // Create Prime Model
      .addCase(createPrimeModel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createPrimeModel.fulfilled,
        (state, action: PayloadAction<PrimeModelCreationData>) => {
          state.loading = false;
          if (action.payload.artifact) {
            state.primeArtifacts.push(action.payload.artifact);
          }
          state.primeArtifactStates.push(...action.payload.states);
          state.primeArtifactTransitions.push(...action.payload.transitions);
          state.primeProcesses.push(...action.payload.processes);
        }
      )
      .addCase(createPrimeModel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create prime model';
      })

      // Update Prime Model
      .addCase(updatePrimeModel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updatePrimeModel.fulfilled,
        (state, action: PayloadAction<PrimeModelCreationData>) => {
          state.loading = false;
          if (action.payload.artifact) {
            const index = state.primeArtifacts.findIndex(
              (a) => a.id === action.payload.artifact?.id
            );
            if (index !== -1) {
              state.primeArtifacts[index] = action.payload.artifact;
            }
          }
          action.payload.states.forEach((newState) => {
            const index = state.primeArtifactStates.findIndex((s) => s.id === newState.id);
            if (index !== -1) {
              state.primeArtifactStates[index] = newState;
            } else {
              state.primeArtifactStates.push(newState);
            }
          });
          action.payload.transitions.forEach((newTransition) => {
            const index = state.primeArtifactTransitions.findIndex(
              (t) => t.id === newTransition.id
            );
            if (index !== -1) {
              state.primeArtifactTransitions[index] = newTransition;
            } else {
              state.primeArtifactTransitions.push(newTransition);
            }
          });
          action.payload.processes.forEach((newProcess) => {
            const index = state.primeProcesses.findIndex((p) => p.id === newProcess.id);
            if (index !== -1) {
              state.primeProcesses[index] = newProcess;
            } else {
              state.primeProcesses.push(newProcess);
            }
          });
        }
      )
      .addCase(updatePrimeModel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update prime model';
      })

      // Delete Prime Model
      .addCase(deletePrimeModel.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePrimeModel.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.primeArtifacts = state.primeArtifacts.filter((a) => a.id !== action.payload);
        state.primeArtifactStates = state.primeArtifactStates.filter(
          (s) => s.prime_artifact_ref !== action.payload
        );
        state.primeArtifactTransitions = state.primeArtifactTransitions.filter((t) =>
          state.primeArtifactStates.some(
            (s) => s.id === t.from_state_ref || s.id === t.to_state_ref
          )
        );
        state.primeProcesses = state.primeProcesses.filter((p) =>
          state.primeArtifactTransitions.some((t) => t.prime_process_ref === p.id)
        );
      })
      .addCase(deletePrimeModel.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete prime model';
      })

      // Update Prime Artifact (from actions file)
      .addCase(updatePrimeArtifact.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePrimeArtifact.fulfilled, (state, action) => {
        state.loading = false;
        console.log('[primeArtifactSlice] Updating artifact in Redux store:', action.payload);

        // Update in primeArtifacts array
        const index = state.primeArtifacts.findIndex((a) => a.id === action.payload.id);
        if (index !== -1) {
          state.primeArtifacts[index] = action.payload;
        }

        // Update in flatArtifacts array
        const flatIndex = state.flatArtifacts.findIndex((a) => a.id === action.payload.id);
        if (flatIndex !== -1) {
          state.flatArtifacts[flatIndex] = action.payload;
        }
      })
      .addCase(updatePrimeArtifact.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update prime artifact';
      })

      // Handle fetchArtifactTypes
      .addCase(fetchArtifactTypes.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchArtifactTypes.fulfilled, (state, action) => {
        state.loading = false;
        state.artifactTypes = action.payload;
      })
      .addCase(fetchArtifactTypes.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch artifact types';
      })

      // Create Prime Artifact Transition
      .addCase(createPrimeArtifactTransition.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        createPrimeArtifactTransition.fulfilled,
        (state, action: PayloadAction<PrimeArtifactStateTransition>) => {
          state.loading = false;
          state.primeArtifactTransitions.push(action.payload);
        }
      )
      .addCase(createPrimeArtifactTransition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create prime artifact transition';
      })

      // Update Prime Artifact Transition
      .addCase(updatePrimeArtifactTransition.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(
        updatePrimeArtifactTransition.fulfilled,
        (state, action: PayloadAction<PrimeArtifactStateTransition>) => {
          state.loading = false;
          const index = state.primeArtifactTransitions.findIndex((t) => t.id === action.payload.id);
          if (index !== -1) {
            state.primeArtifactTransitions[index] = action.payload;
          }
        }
      )
      .addCase(updatePrimeArtifactTransition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update prime artifact transition';
      })

      // Delete Prime Artifact Transition
      .addCase(deletePrimeArtifactTransition.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePrimeArtifactTransition.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.primeArtifactTransitions = state.primeArtifactTransitions.filter(
          (t) => t.id !== action.payload
        );
      })
      .addCase(deletePrimeArtifactTransition.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete prime artifact transition';
      })

      // Create Prime Process
      .addCase(createPrimeProcess.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createPrimeProcess.fulfilled, (state, action: PayloadAction<PrimeProcess>) => {
        state.loading = false;
        state.primeProcesses.push(action.payload);
      })
      .addCase(createPrimeProcess.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to create prime process';
      })

      // Update Prime Process
      .addCase(updatePrimeProcess.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updatePrimeProcess.fulfilled, (state, action: PayloadAction<PrimeProcess>) => {
        state.loading = false;
        const index = state.primeProcesses.findIndex((p) => p.id === action.payload.id);
        if (index !== -1) {
          state.primeProcesses[index] = action.payload;
        }
      })
      .addCase(updatePrimeProcess.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to update prime process';
      })

      // Delete Prime Process
      .addCase(deletePrimeProcess.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deletePrimeProcess.fulfilled, (state, action: PayloadAction<string>) => {
        state.loading = false;
        state.primeProcesses = state.primeProcesses.filter((p) => p.id !== action.payload);
      })
      .addCase(deletePrimeProcess.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to delete prime process';
      });
  },
});

export const { clearPrimeArtifactState } = primeArtifactSlice.actions;
export default primeArtifactSlice.reducer;
